import { readNativeFile } from "./native-file";
import { LINK_ROOTS } from "./links";
import { beginRecoverableCapture, finishRecoverableCapture } from "./native-media-recovery";
import { clearAppBadge, clearLocalNotices, presentForegroundNotice, watchLocalNoticeActions } from "./native-notifications";
import { requestBrowserNotifications, type PushPermissionResult } from "./browser-push";
import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from "@capacitor/core";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();
export const NATIVE_CONTRACT_VERSION = 1;
type NativeShareItem = {
  type: string;
  value: string;
  path?: string;
  mimeType?: string;
};
const HostedOriginGuard = registerPlugin<{
  getCapabilities(): Promise<{ version: number; origin: string }>;
  getPendingShares(): Promise<{ batchId?: string; items: NativeShareItem[] }>;
  acknowledgeShares(options: { batchId: string }): Promise<void>;
  addListener(
    event: "shareReceived",
    callback: () => void,
  ): Promise<PluginListenerHandle>;
}>("HostedOriginGuard");
export async function installedNativeContract() {
  if (!isNative()) return null;
  requirePlugin("HostedOriginGuard");
  return HostedOriginGuard.getCapabilities();
}

// Availability reflects the installed binary, not the latest website's assumptions.
export function capabilities() {
  return {
    native: isNative(),
    phone: Capacitor.isPluginAvailable("FirebaseAuthentication"),
    push: Capacitor.isPluginAvailable("FirebaseMessaging"),
    badges: isNative() && Capacitor.isPluginAvailable("Badge"),
    localNotifications: isNative() && Capacitor.isPluginAvailable("LocalNotifications"),
    camera: Capacitor.isPluginAvailable("Camera"),
    haptics: isNative() && Capacitor.isPluginAvailable("Haptics"),
    browser: isNative() && Capacitor.isPluginAvailable("Browser"),
    mediaPicker: Capacitor.isPluginAvailable("FilePicker"),
    videoCapture: Capacitor.isPluginAvailable("SeasonVideo"),
    audioRecording: Capacitor.isPluginAvailable("VoiceRecorder"),
    filesystem: Capacitor.isPluginAvailable("Filesystem"),
    share: Capacitor.isPluginAvailable("Share"),
    settings: Capacitor.isPluginAvailable("NativeSettings"),
    incomingShares:
      isNative() && Capacitor.isPluginAvailable("HostedOriginGuard"),
  };
}

function requirePlugin(name: string) {
  if (!isNative() || !Capacitor.isPluginAvailable(name)) {
    throw new Error(
      `This version of the app does not support ${name}. Please update the app.`,
    );
  }
}

export function trustedRoute(url: string, origin: string): string | null {
  try {
    const parsed = new URL(url, origin);
    if (parsed.origin !== origin && parsed.protocol !== "liftandlearn:")
      return null;
    const path =
      parsed.protocol === "liftandlearn:"
        ? `/${parsed.hostname}${parsed.pathname}`
        : parsed.pathname;
    const root = path.split("/")[1] ?? "";
    if (!(LINK_ROOTS as readonly string[]).includes(root)) return null;
    return `${path}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

let cancelPhone: (() => void) | undefined;
let automaticToken: string | undefined;
export const AUTOMATIC_PHONE_VERIFICATION = "native-automatic";

export async function nativePhoneStart(phoneNumber: string): Promise<string> {
  requirePlugin("FirebaseAuthentication");
  cancelPhone?.();
  automaticToken = undefined;
  const { FirebaseAuthentication: auth } = await import(
    "@capacitor-firebase/authentication"
  );
  return new Promise<string>((resolve, reject) => {
    const handles: PluginListenerHandle[] = [];
    let settled = false;
    const cleanup = () => {
      handles.splice(0).forEach((h) => void h.remove());
      clearTimeout(timeout);
    };
    const finish = (value?: string, error?: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      cancelPhone = undefined;
      if (error) reject(error);
      else resolve(value!);
    };
    const timeout = setTimeout(
      () =>
        finish(
          undefined,
          new Error("Phone verification timed out. Try again."),
        ),
      120_000,
    );
    cancelPhone = () =>
      finish(
        undefined,
        new Error("Phone verification was replaced by another request."),
      );
    void (async () => {
      handles.push(
        await auth.addListener("phoneCodeSent", (e) =>
          finish(e.verificationId),
        ),
      );
      handles.push(
        await auth.addListener("phoneVerificationFailed", (e) =>
          finish(undefined, new Error(e.message)),
        ),
      );
      handles.push(
        await auth.addListener("phoneVerificationCompleted", () => {
          void auth
            .getIdToken()
            .then(({ token }) => {
              automaticToken = token;
              finish(AUTOMATIC_PHONE_VERIFICATION);
            })
            .catch((error) => finish(undefined, error));
        }),
      );
      if (settled) {
        cleanup();
        return;
      }
      await auth.signInWithPhoneNumber({ phoneNumber });
    })().catch((error) => finish(undefined, error));
  });
}

export async function nativePhoneVerify(
  verificationId: string,
  code: string,
): Promise<string> {
  requirePlugin("FirebaseAuthentication");
  if (verificationId === AUTOMATIC_PHONE_VERIFICATION && automaticToken) {
    const token = automaticToken;
    automaticToken = undefined;
    return token;
  }
  const { FirebaseAuthentication: auth } = await import(
    "@capacitor-firebase/authentication"
  );
  await auth.confirmVerificationCode({
    verificationId,
    verificationCode: code,
  });
  return (await auth.getIdToken()).token;
}

export async function nativeSignOut() {
  await clearLocalNotices();
  await clearAppBadge();
  if (!capabilities().phone) return;
  const { FirebaseAuthentication } = await import(
    "@capacitor-firebase/authentication"
  );
  await FirebaseAuthentication.signOut();
}

export async function openNotificationSettings() {
  requirePlugin("NativeSettings");
  const { NativeSettings, AndroidSettings, IOSSettings } = await import(
    "capacitor-native-settings"
  );
  await NativeSettings.open({
    optionAndroid: AndroidSettings.ApplicationDetails,
    optionIOS: IOSSettings.App,
  });
}

export async function requestNotifications(): Promise<PushPermissionResult> {
  if (!isNative()) return requestBrowserNotifications();
  requirePlugin("FirebaseMessaging");
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const prior = await FirebaseMessaging.checkPermissions();
  if (prior.receive === "denied") {
    await openNotificationSettings();
    return { granted: false };
  }
  const permission =
    prior.receive === "granted"
      ? prior
      : await FirebaseMessaging.requestPermissions();
  if (permission.receive !== "granted") return { granted: false };
  const { token } = await FirebaseMessaging.getToken();
  return { granted: true, token };
}

export async function watchNativeEvents(handlers: {
  onToken: (token: string) => void;
  onError?: (error: unknown) => void;
  onNotification: (data: Record<string, unknown>) => void;
  onForegroundNotification?: (notification: { title?: string; body?: string; data: Record<string, unknown> }) => void;
  onRoute: (route: string) => void;
  onResume?: () => void;
  onShare?: () => void;
}): Promise<() => void> {
  if (!isNative()) return () => {};
  const handles: PluginListenerHandle[] = [];
  try {
    const { App } = await import("@capacitor/app");
    const localActions = await watchLocalNoticeActions(handlers.onNotification);
    if (localActions) handles.push(localActions);
    const messaging = capabilities().push
      ? (await import("@capacitor-firebase/messaging")).FirebaseMessaging
      : undefined;
    const reconcilePush = async () => {
      if (!messaging) return;
      try {
        const FirebaseMessaging = messaging;
        if ((await FirebaseMessaging.checkPermissions()).receive === "granted") {
          handlers.onToken((await FirebaseMessaging.getToken()).token);
        }
      } catch (error) { handlers.onError?.(error); }
    };


    if (platform() === "android" && capabilities().incomingShares) {
      handles.push(
        await HostedOriginGuard.addListener("shareReceived", () =>
          handlers.onShare?.(),
        ),
      );
    }
    const route = (url: string) => {
      if (url.startsWith("liftandlearn://share")) {
        handlers.onShare?.();
        return;
      }
      const safe = trustedRoute(url, window.location.origin);
      if (safe) handlers.onRoute(safe);
    };
    handles.push(
      await App.addListener("appUrlOpen", (event) => route(event.url)),
    );
    handles.push(
      await App.addListener("appStateChange", (event) => {
        if (event.isActive) { handlers.onResume?.(); void reconcilePush(); }
      }),
    );
    void reconcilePush();
    const launch = await App.getLaunchUrl();
    if (launch) route(launch.url);
    if (messaging) {
      const FirebaseMessaging = messaging;
      handles.push(
        await FirebaseMessaging.addListener("tokenReceived", (event) =>
          handlers.onToken(event.token),
        ),
      );
      handles.push(await FirebaseMessaging.addListener("notificationReceived", (event) => {
        const notification = event.notification;
        const notice = { title: notification.title || "Lift & Learn", body: notification.body || "", data: (notification.data ?? {}) as Record<string, unknown> };
        const fallback = () => handlers.onForegroundNotification?.(notice);
        // iOS already presents a system alert. Android uses local notifications;
        // older shells retain the in-app banner without double presentation.
        if (platform() === "ios") return;
        void presentForegroundNotice({ ...notice, key: notification.id || String(notice.data.notification_id || JSON.stringify(notice)) })
          .then(presented => { if (!presented) fallback(); })
          .catch(error => { handlers.onError?.(error); fallback(); });
      }));
      handles.push(
        await FirebaseMessaging.addListener(
          "notificationActionPerformed",
          (event) => {
            handlers.onNotification(
              (event.notification.data ?? {}) as Record<string, unknown>,
            );
          },
        ),
      );
    }
  } catch (error) {
    await Promise.all(handles.map((handle) => handle.remove()));
    throw error;
  }
  return () => {
    handles.forEach((handle) => void handle.remove());
  };
}

export async function capturePhoto(
  source: "camera" | "photos" = "photos",
  purpose: "post" | "profile" = "post",
): Promise<File> {
  if (source === "photos") {
    const [photo] = await nativePickMedia("photos", 1);
    if (!photo) throw new Error("Photo selection cancelled.");
    return photo;
  }
  requirePlugin("Camera");
  const { Camera, CameraResultType, CameraSource } = await import(
    "@capacitor/camera"
  );
  const captureId = beginRecoverableCapture("Camera", purpose);
  try {
  const photo = await Camera.getPhoto({
    quality: 88,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    correctOrientation: true,
  });
  if (!photo.path) throw new Error("The selected photo could not be read.");
  return new File([await readNativeFile(photo.path)], `photo.${photo.format}`, {
    type: `image/${photo.format}`,
  });
  } finally { finishRecoverableCapture(captureId); }
}

/** Capacitor iOS rejects an ordinary share-sheet dismissal with this exact message. */
export function isShareCancelled(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const value = error as { name?: unknown; message?: unknown };
  return value.name === "AbortError" || value.message === "Share canceled";
}

export async function shareUrl(url: string, title: string) {
  if (!/^https:\/\//i.test(url))
    throw new Error("Only secure web links can be shared.");
  if (capabilities().share) {
    const { Share } = await import("@capacitor/share");
    try {
      await Share.share({ url, title });
    } catch (error) {
      if (isShareCancelled(error)) throw new DOMException("Sharing cancelled", "AbortError");
      throw error;
    }
  } else if (navigator.share) await navigator.share({ url, title });
  else await navigator.clipboard.writeText(url);
}

export async function nativePendingShares(): Promise<{
  batchId: string;
  text: string;
  files: File[];
} | null> {
  if (!capabilities().incomingShares) return null;
  const pending = await HostedOriginGuard.getPendingShares();
  if (!pending.batchId || !pending.items.length) return null;
  const files: File[] = [];
  const text: string[] = [];
  for (const item of pending.items) {
    if (item.type === "text" || item.type === "url") text.push(item.value);
    else if (item.path) {
      if (item.type === "video" || item.mimeType?.startsWith("video/") || /\.(mp4|mov|m4v|webm|3gp)$/i.test(item.value)) {
        files.push(await nativePrepareVideo(item.path));
        continue;
      }
      files.push(new File([await readNativeFile(item.path)], item.value, {
        type: item.mimeType || "application/octet-stream",
      }));
    }
  }
  return { batchId: pending.batchId, text: text.join("\n"), files };
}

// Only acknowledge after the composer has persisted these File bytes locally.
export async function nativeAcknowledgeShares(batchId: string): Promise<void> {
  requirePlugin("HostedOriginGuard");
  await HostedOriginGuard.acknowledgeShares({ batchId });
}

/** Native system pickers keep gallery/files access scoped to the chosen items. */
export async function nativePickMedia(
  kind: "photos" | "videos" | "media" | "audio" | "files",
  limit = 1,
): Promise<File[]> {
  requirePlugin("FilePicker");
  const { FilePicker } = await import("@capawesome/capacitor-file-picker");
  const result = kind === "media"
    ? await FilePicker.pickMedia({ limit, readData: false, skipTranscoding: false })
    : kind === "photos"
    ? await FilePicker.pickImages({ limit, readData: false })
    : kind === "videos"
      ? await FilePicker.pickVideos({ limit, readData: false, skipTranscoding: false })
      : await FilePicker.pickFiles({ limit, readData: false, types: kind === "audio" ? ["audio/*"] : ["image/*", "video/*", "audio/*"] });
  const files: File[] = [];
  for (const item of result.files) {
    if (!item.path) throw new Error("The selected file could not be read.");
    let path = item.path;
    let name = item.name;
    let type = item.mimeType;
    if (platform() === "ios" && (type === "image/heic" || type === "image/heif" || /\.hei[cf]$/i.test(name))) {
      path = (await FilePicker.convertHeicToJpeg({ path })).path;
      name = name.replace(/\.[^.]+$/, "") + ".jpg";
      type = "image/jpeg";
    }
    if (type.startsWith("video/") || /\.(mp4|mov|m4v|webm|3gp)$/i.test(name)) {
      files.push(await nativePrepareVideo(path));
      continue;
    }
    files.push(new File([await readNativeFile(path)], name, { type }));
  }
  return files;
}

type PreparedVideo = { path: string; name: string; mimeType: string; size: number; duration: number };
const SeasonVideo = registerPlugin<{
  capture(options: { maxDurationSeconds: number }): Promise<PreparedVideo>;
  prepare(options: { path: string; maxDurationSeconds: number }): Promise<PreparedVideo>;
  cancel(): Promise<void>;
}>("SeasonVideo");
export const MAX_NATIVE_VIDEO_SECONDS = 120;
async function videoFile(result: PreparedVideo): Promise<File> {
  return new File([await readNativeFile(result.path)], result.name, { type: result.mimeType });
}
/** Compression occurs on device before the upload's 25 MB validation. */
export async function nativePrepareVideo(path: string): Promise<File> {
  requirePlugin("SeasonVideo");
  return videoFile(await SeasonVideo.prepare({ path, maxDurationSeconds: 3600 }));
}
export async function nativeCaptureVideo(): Promise<File> {
  requirePlugin("SeasonVideo");
  const captureId = beginRecoverableCapture("SeasonVideo");
  try { return await videoFile(await SeasonVideo.capture({ maxDurationSeconds: MAX_NATIVE_VIDEO_SECONDS })); }
  finally { finishRecoverableCapture(captureId); }
}
export async function nativeCancelVideo(): Promise<void> {
  if (capabilities().videoCapture) await SeasonVideo.cancel();
}


/** Haptics enrich confirmed actions; lack of hardware must never fail the action. */
export async function nativeFeedback(kind: "success" | "selection"): Promise<void> {
  if (!capabilities().haptics) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import("@capacitor/haptics");
    if (kind === "success") await Haptics.notification({ type: NotificationType.Success });
    else await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* Haptic support is optional and does not affect a completed action. */ }
}

export function externalBrowserUrl(href: string, origin: string): string | null {
  try {
    const url = new URL(href, origin);
    if (url.protocol !== "https:" || url.origin === origin || url.username || url.password) return null;
    return url.href;
  } catch { return null; }
}

/** Only ordinary user-clicked external HTTPS anchors leave the privileged WebView. */
export function watchNativeExternalLinks(onError: (error: unknown) => void): () => void {
  if (!capabilities().browser) return () => {};
  const click = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute("download")) return;
    const url = externalBrowserUrl(anchor.href, window.location.origin);
    if (!url) return;
    event.preventDefault();
    void import("@capacitor/browser").then(({ Browser }) => Browser.open({ url, presentationStyle: "fullscreen" })).catch(onError);
  };
  document.addEventListener("click", click);
  return () => document.removeEventListener("click", click);
}
